import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { NotificationManager } from "react-notifications";
import IssueOptionsDialog from "./IssueOptionsDialog";
import SampleSelectionChecklist from "./SampleSelectionChecklist";
import { PrintBottleOrderLabelsByLabOrder } from "../../services/barcodeService";

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1),
}));

const RadioSelect = ({ value, text, onChange, onNoSelect }) => {
  const handleChange = useCallback(
    (e) => {
      const isYes = e.target.value === "yes";
      onChange(isYes);

      if (!isYes && onNoSelect) {
        onNoSelect();
      }
    },
    [onChange, onNoSelect]
  );

  return (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
      <Grid item xs={12} md={8}>
        <FormLabel component="legend" sx={{ fontWeight: "medium" }}>
          {text}
        </FormLabel>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={value === true ? "yes" : value === false ? "no" : ""}
            onChange={handleChange}
          >
            <FormControlLabel
              value="yes"
              control={<Radio size="small" />}
              label="Yes"
            />
            <FormControlLabel
              value="no"
              control={<Radio size="small" />}
              label="No"
            />
          </RadioGroup>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default function SampleScannedFormNew(props) {
  const { register, handleSubmit, control, setValue, watch } = useForm({
    defaultValues: props.model,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [activeField, setActiveField] = useState("");

  const questionsConfig = {
    cocCompleted: {
      text: "Chain of Custody is present & filled out completely in ink and legible?",
      fieldName: "cocCompletedComments",
      options: [
        { id: "1", text: "Client did not relinquish the COC" },
        {
          id: "2",
          text: "Client did not provide collection information on the COC, we took the information from the sample bottles and applied it to the COC.",
        },
        {
          id: "3",
          text: "Client did not provide collection information on the COC, we contacted the client who provided us with the information and permission to record it on the COC.",
        },
        { id: "Other", text: "Other" },
      ],
    },

    correctLabels: {
      text: "Sample Labels match COC? (Sample IDs, Test Names, Collection Dates & Times)",
      fieldName: "correctLabelsComments",
      options: [
        {
          id: "4",
          text: "Collection information not recorded on the sample bottles. We took the information from the COC and recorded it on the sample bottles",
        },
        {
          id: "5",
          text: "Collection information on the sample bottles did not match the information on the COC. We corrected the information on the sample bottles to match the COC",
        },
        { id: "Other", text: "Other" },
      ],
    },
    isRush: {
      text: "Rush TAT requested (5 days or less)?",
      fieldName: "rushTatComments",
      options: [
        { id: "1day", text: "1 day Rush" },
        { id: "3day", text: "3 days Rush" },
        { id: "5day", text: "5 days Rush" },
        { id: "Other", text: "Other" },
      ],
    },
  };

  const shouldShowPrePostPayQuestions =
    props.model.orderPayType === null || props.model.orderPayType === "";

  // Require at least one sample selected when there are samples to pick from.
  // Orders without sample data available fall back to the old unrestricted behavior.
  // Updating a submitted sheet has no selection to make, so the rule doesn't apply.
  const hasSamplesToPick = !props.isEditing && (props.unsubmittedSamples || []).length > 0;
  const nothingSelected = hasSamplesToPick && (props.selectedSampleIds || []).length === 0;

  useEffect(() => {
    setValue("headSpacePresent", props.model.headSpacePresent);
  }, [props.model.headSpacePresent, setValue]);


  const handleBottleLabelsPrint =  () => {    
    try {
       PrintBottleOrderLabelsByLabOrder("sampleLabel", props.orderNumber);
      // NotificationManager.success("Bottle labels printed successfully");
    } catch (error) {
      NotificationManager.error("Error printing bottle labels");
    }
  };

  const handleBottleBarCodePrint =  () => {    
    try {
       PrintBottleOrderLabelsByLabOrder("sampleBarcode", props.orderNumber);
      // NotificationManager.success("Barcode labels printed successfully");
    } catch (error) {
      NotificationManager.error("Error printing barcode labels");
    }
  };

  const onResetHeadSpace = () => {
    setValue("headSpacePresent", null);
    props.onResetHeadSpace();
  };

  const handleSaveAnswer = (answer) => {
    if (activeField && currentQuestion) {
      const fieldName = questionsConfig[activeField].fieldName;
      setValue(fieldName, answer);
    }
    setDialogOpen(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      {!props.isLoading && (
        <form onSubmit={handleSubmit(props.onSubmit)}>
          {/* Header Section */}
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              {props.webUrl && (
                <Button
                  variant="contained"
                  size="small"
                  href={props.webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Form
                </Button>
              )}
              {props.model.willRushPay === true && (
                <Chip label="Need Rush" color="error" size="small" />
              )}
              {props.model.willRushPay === false && (
                <Chip label="Not Paid for Rush" color="error" size="small" />
              )}
              {(!shouldShowPrePostPayQuestions) && props.model.isKitPaid && (
                <Chip label="Kit Paid" color="primary" size="small" />
              )}
              {(!shouldShowPrePostPayQuestions) && !props.model.isKitPaid && (
                <Chip label="Kit Not Paid" color="warning" size="small" />
              )}

              {props.model.selectedKit?.trim() && (
                <Chip
                  label={`Kit: ${props.model.selectedKit}`}
                  color="primary"
                  size="small"
                />
              )}

              <Button
                variant="outlined"
                size="small"
                onClick={handleBottleLabelsPrint}
                startIcon={<LocalPrintshopIcon />}
                sx={{ color: "#0eecf0", borderColor: "#0eecf0" }}
              >
                Bottle Labels
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleBottleBarCodePrint}
                startIcon={<QrCodeIcon />}
                sx={{ color: "#8911f2", borderColor: "#8911f2" }}
              >
                Barcode Labels
              </Button>

            </Stack>
          </Paper>

          <SampleSelectionChecklist
            samples={props.isEditing ? props.sheetSamples : props.unsubmittedSamples}
            selectedIds={props.selectedSampleIds || []}
            onChange={props.onSelectedSampleIdsChange}
            checkInSheetCount={props.checkInSheetCount}
            totalSampleCount={props.totalSampleCount}
            remainingSampleCount={props.remainingSampleCount}
            readOnly={props.isEditing}
            sheetNumber={props.sheetNumber}
          />

          {/* Received Date & Delivery Method */}
          <StyledCard>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Received Date"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    {...register("dateReceied")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Delivery Method</InputLabel>
                    <Select size="small" label="Delivery Method" {...register("deliveryMethod")}>
                      <MenuItem value="PickUp">ELI Route</MenuItem>
                      <MenuItem value="ClientWalkIn">Walk-in</MenuItem>
                      <MenuItem value="FedEx">FedEx</MenuItem>
                      <MenuItem value="USPS">USPS</MenuItem>
                      <MenuItem value="UPS">UPS</MenuItem>
                      <MenuItem value="">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Cooler Temperature & Sample Received On */}
          {shouldShowPrePostPayQuestions && (
            <StyledCard>
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={8}>
                        <Typography variant="body1">
                          Cooler temperature upon lab receipt (℃)
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ step: "0.01" }}
                          {...register("coolerTemperature")}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={8}>
                        <Typography variant="body1">Sample received on</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl size="small" fullWidth>
                          <Select {...register("receivedOn")}>
                            <MenuItem value="Wet Ice">Wet Ice</MenuItem>
                            <MenuItem value="Ice Packs">Ice Packs</MenuItem>
                            <MenuItem value="None">None</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          )}

          {/* Comments */}
          <StyledCard>
            <CardContent>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Lab Comments"
                placeholder="Lab Comments"
                {...register("labComments")}
              />
            </CardContent>
          </StyledCard>

          {/* Submit */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="success"
              size="large"
              disabled={props.isLoading || nothingSelected}
              title={nothingSelected ? "Select at least one sample to submit" : undefined}
              startIcon={props.isLoading ? <CircularProgress size={20} /> : null}
            >
              {props.isLoading
                ? props.isEditing
                  ? "Updating..."
                  : "Submitting..."
                : props.isEditing
                ? `Update Check-In Sheet #${props.sheetNumber}`
                : "Submit"}
            </Button>
          </Box>

          {/* Hidden fields */}
          <input type="hidden" {...register("cocCompletedComments")} />
          <input type="hidden" {...register("correctLabelsComments")} />
          <input type="hidden" {...register("rushTatComments")} />
        </form>
      )}

      {props.isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      <IssueOptionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        question={currentQuestion?.text}
        options={currentQuestion?.options || []}
        onSave={handleSaveAnswer}
      />
    </Box>
  );
}